import { getAllUsersWithClaimsInGroupInterview } from '@modules/api';
import { userMessage } from '@modules/api-error';
import { logError } from '@modules/logging';
import { useInterviewOptions } from '@providers/InterviewsProvider';
import { UserWithClaims } from '@synergetx/puppet-interview-types';
import { useState, useEffect, useMemo, useCallback } from 'react';

const LOAD_FAILED = 'We could not load the users for this project.';

export const useUsersWithClaims = () => {
    const [users, setUsers] = useState<UserWithClaims[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const { selectedInterview } = useInterviewOptions();
    const groupId = selectedInterview?.groupId;

    const loadUsers = useCallback(async (): Promise<UserWithClaims[] | null> => {
        if (!groupId) return null;

        try {
            return await getAllUsersWithClaimsInGroupInterview(groupId);
        } catch (caught) {
            logError(caught as Error);
            setError(userMessage(caught, LOAD_FAILED));
            // Settle on an empty list rather than leaving `users` null, which the screen
            // reads as "still loading" and would spin forever.
            return [];
        }
    }, [groupId]);

    const refreshUsers = useCallback(async () => {
        setUsers(null);
        setError(null);
        setUsers(await loadUsers());
    }, [loadUsers]);

    // Reset loading state during render when groupId changes (React recommended pattern)
    const [prevGroupId, setPrevGroupId] = useState(groupId);
    if (groupId !== prevGroupId) {
        setPrevGroupId(groupId);
        setUsers(null);
        setError(null);
    }

    useEffect(() => {
        let cancelled = false;

        (async () => {
            const allUsers = await loadUsers();
            if (!cancelled) setUsers(allUsers);
        })();

        return () => {
            cancelled = true;
        };
    }, [loadUsers]);

    return useMemo(
        () => ({ error, refreshUsers, setUsers, users }),
        [error, refreshUsers, setUsers, users],
    );
};
